import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as TenantIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Domain as DomainIcon,
  CreditCard as SubIcon,
} from '@mui/icons-material';
import { Tenant, TenantInput } from '../types';
import tenantService from '../services/tenantService';
import { useUserRole } from '../hooks/useUserRole';
import TenantSubscriptionPanel from '../components/TenantSubscriptionPanel';

interface DialogState {
  open: boolean;
  mode: 'create' | 'edit';
  tenant?: Tenant;
}

const Tenants: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<DialogState>({ open: false, mode: 'create' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // User role check
  const { isAdmin: hasControlPanelAccess, isLoading: userLoading, user } = useUserRole();
  const isSuperAdmin = user?.roleId === 1;

  // Subscription panel state
  const [subPanel, setSubPanel] = useState<{ open: boolean; tenantId: string; tenantName: string }>(
    { open: false, tenantId: '', tenantName: '' },
  );

  // Form state
  const [formData, setFormData] = useState<TenantInput>({
    name: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tenantService.getTenants();
      setTenants(data || []);
    } catch (err: any) {
      console.error('Error loading tenants:', err);
      setError(err.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode: 'create' | 'edit', tenant?: Tenant) => {
    if (mode === 'create') {
      setFormData({
        name: '',
        description: '',
        isActive: true
      });
    } else if (tenant) {
      setFormData({
        name: tenant.name,
        description: tenant.description || '',
        isActive: tenant.isActive
      });
    }
    
    setDialogState({ open: true, mode, tenant });
  };

  const handleCloseDialog = () => {
    setDialogState({ open: false, mode: 'create' });
    setFormData({
      name: '',
      description: '',
      isActive: true
    });
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      
      if (!formData.name) {
        setError('Name is required');
        return;
      }
      
      if (dialogState.mode === 'create') {
        await tenantService.createTenant(formData);
        setSuccess('Tenant created successfully!');
      } else if (dialogState.tenant) {
        await tenantService.updateTenant(dialogState.tenant.id, formData);
        setSuccess('Tenant updated successfully!');
      }
      
      handleCloseDialog();
      await loadTenants();
    } catch (err: any) {
      console.error('Error saving tenant:', err);
      setError(err.message || 'Failed to save tenant');
    }
  };

  const handleDelete = async (tenant: Tenant) => {
    if (!window.confirm(`Are you sure you want to delete "${tenant.name}"?`)) {
      return;
    }

    try {
      setError(null);
      await tenantService.deleteTenant(tenant.id);
      setSuccess('Tenant deleted successfully!');
      await loadTenants();
    } catch (err: any) {
      console.error('Error deleting tenant:', err);
      setError(err.message || 'Failed to delete tenant');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (isActive: boolean) => {
    return isActive ? 
      <Chip label="Active" color="success" size="small" icon={<ActiveIcon />} /> : 
      <Chip label="Inactive" color="default" size="small" icon={<InactiveIcon />} />;
  };

  const filteredTenants = tenants.filter((tenant: Tenant) =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tenant.description && tenant.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const paginatedTenants = filteredTenants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Clear success/error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (userLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasControlPanelAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access this page. Control Panel access requires role ID ≤ 2.
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Tenants
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage organizational tenants in the PECS system
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
          sx={{ borderRadius: 2 }}
        >
          Add Tenant
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'primary.light',
                    color: 'primary.main',
                    mr: 2
                  }}
                >
                  <TenantIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {tenants.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Tenants
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'success.light',
                    color: 'success.main',
                    mr: 2
                  }}
                >
                  <ActiveIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {tenants.filter(t => t.isActive).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Tenants
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'warning.light',
                    color: 'warning.main',
                    mr: 2
                  }}
                >
                  <InactiveIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {tenants.filter(t => !t.isActive).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inactive Tenants
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'info.light',
                    color: 'info.main',
                    mr: 2
                  }}
                >
                  <DomainIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {tenants.filter(t => t.description).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    With Description
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search tenants by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {/* Data Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm ? 'No tenants found matching your search.' : 'No tenants available.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTenants.map((tenant) => (
                  <TableRow key={tenant.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {tenant.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {tenant.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(tenant.isActive)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(tenant.createdDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Tenant">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('edit', tenant)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {isSuperAdmin && (
                          <Tooltip title="Manage Subscription">
                            <IconButton
                              size="small"
                              onClick={() => setSubPanel({ open: true, tenantId: tenant.id, tenantName: tenant.name })}
                              color="secondary"
                            >
                              <SubIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Tenant">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(tenant)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {filteredTenants.length > rowsPerPage && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredTenants.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogState.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogState.mode === 'create' ? 'Create New Tenant' : 'Edit Tenant'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.isActive ? 'true' : 'false'}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                label="Status"
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 
             dialogState.mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription management panel (super-admin only) */}
      <TenantSubscriptionPanel
        open={subPanel.open}
        tenantId={subPanel.tenantId}
        tenantName={subPanel.tenantName}
        onClose={() => setSubPanel({ ...subPanel, open: false })}
      />
    </Container>
  );
};

export default Tenants;
